import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useRef, useState } from 'react';

import { PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { useIsLandscapeViewIndicatorProductPurchased, useLandscapeView } from '@/hooks/useLandscapeView';

export const NO_RADIUS = 'none';

export type RadiusType = '5' | '10' | '15' | '20' | '25' | '30' | typeof NO_RADIUS;

export const RADIUS_LABEL_MAPPING = {
  [NO_RADIUS]: '0.0',
  '5': '5000.0',
  '10': '10000.0',
  '15': '15000.0',
  '20': '20000.0',
  '25': '25000.0',
  '30': '30000.0',
} as const;

type MapRadiusContextType = {
  radius: RadiusType;
  setRadius: Dispatch<SetStateAction<RadiusType>>;
};

export const MapRadiusContext = createContext<MapRadiusContextType | null>(null);

export const MapRadiusContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const isLandscapeFeatureActive = useIsLandscapeViewIndicatorProductPurchased();
  const [radius, setRadius] = useState<RadiusType>(NO_RADIUS);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && isLandscapeFeatureActive) {
      hasInitialized.current = true;
      setRadius('30');
    }
  }, [isLandscapeFeatureActive, hasInitialized, setRadius]);

  return <MapRadiusContext.Provider value={{ radius, setRadius }}>{children}</MapRadiusContext.Provider>;
};

export const useMapRadiusContext = (): MapRadiusContextType => {
  const context = useContext(MapRadiusContext);

  if (!context) {
    throw new Error(
      `The component that calls useMapRadiusContext() needs to be a deep child of <MapRadiusContextProvider>`,
    );
  }

  return context;
};

export const useGetRadiusLabel = () => {
  const { radius } = useMapRadiusContext();
  const { isActive } = useLandscapeView();

  if (!isActive) {
    return {
      [PlotReportFactLabelNameEnum.landscape_radius_m]: RADIUS_LABEL_MAPPING[`${NO_RADIUS}`],
    };
  }

  return {
    [PlotReportFactLabelNameEnum.landscape_radius_m]: RADIUS_LABEL_MAPPING[`${radius}`],
  };
};
